import ToggleSwitch from '../default_toggle';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  InnerContainer,
  ButtonContainer,
  SettingsActionButton,
  SettingsLogoutButton,
  ThemeSection,
  ThemeDescription,
  ThemeTip,
  ThemeTipPointer,
  ThemeTipDismiss,
  NewFeatureBadge,
  PreferenceSection,
  PreferenceTitle,
  PreferenceToggleRow,
  NavBalloon,
} from './styles'
import ResetPassword from '../reset_password';
import { eraseAll } from '../../store/modules/app_data/actions';
import { useNavigate } from 'react-router-dom';
import { useThemeMode, STORAGE_KEY as THEME_STORAGE_KEY } from '../../contexts/ThemeContext';
import {
  hasSeenLightModeHint,
  markLightModeHintSeen,
  LIGHT_MODE_HINT_KEY,
} from '../../utils/themeFeatureHint';
import { useModalContext } from '../CustomModal/ModalProvider.jsx';
import { userService } from '../../services/userService';
import { isFirmsEmailOptIn, readUiSession } from '../../utils/authSession';

const Settings = () => {

  const dispatch = useDispatch();
  const collapsed = useSelector((state) => state.sidebar);
  const currentUser = useSelector((state) => state.user.currentUser);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showThemeHint, setShowThemeHint] = useState(() => !hasSeenLightModeHint());
  const [isSavingTheme, setIsSavingTheme] = useState(false);
  const [receiveFirmsEmails, setReceiveFirmsEmails] = useState(() =>
    isFirmsEmailOptIn(readUiSession()?.receive_firms_emails)
  );
  const [loadingFirms, setLoadingFirms] = useState(true);
  const [savingFirms, setSavingFirms] = useState(false);
  const { isDark, persistTheme } = useThemeMode();
  const { showError } = useModalContext();

  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadFirmsPreference = async () => {
      setLoadingFirms(true);
      try {
        const data = await userService.getFirmsEmailPreference();
        if (!cancelled) {
          setReceiveFirmsEmails(isFirmsEmailOptIn(data?.receive_firms_emails));
        }
      } catch (error) {
        console.error('Erro ao carregar preferência FIRMS:', error);
        // Mantém valor da sessão UI / default false
      } finally {
        if (!cancelled) setLoadingFirms(false);
      }
    };

    loadFirmsPreference();
    return () => {
      cancelled = true;
    };
  }, []);

  const dismissThemeHint = () => {
    markLightModeHintSeen();
    setShowThemeHint(false);
    window.dispatchEvent(new CustomEvent('vert-light-mode-hint-seen'));
  };

  const handleThemeToggle = async () => {
    const nextTheme = isDark ? 'light' : 'dark';
    setIsSavingTheme(true);
    try {
      await persistTheme(nextTheme);
      if (showThemeHint) {
        dismissThemeHint();
      }
    } catch (error) {
      console.error('Erro ao salvar tema:', error);
      showError(
        'Não foi possível salvar o tema',
        'A aparência local foi revertida. Verifique sua conexão e tente novamente.'
      );
    } finally {
      setIsSavingTheme(false);
    }
  };

  const handleFirmsToggle = async () => {
    const next = !receiveFirmsEmails;
    setReceiveFirmsEmails(next);
    setSavingFirms(true);
    try {
      const data = await userService.updateFirmsEmailPreference(next);
      setReceiveFirmsEmails(isFirmsEmailOptIn(data?.receive_firms_emails));
    } catch (error) {
      console.error('Erro ao salvar preferência FIRMS:', error);
      setReceiveFirmsEmails(!next);
      showError(
        'Não foi possível salvar a preferência de e-mails FIRMS',
        'Verifique sua conexão e tente novamente.'
      );
    } finally {
      setSavingFirms(false);
    }
  };

  const handleLogout = async () => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    const savedHint = localStorage.getItem(LIGHT_MODE_HINT_KEY);
    try {
      await userService.logout();
    } catch (error) {
      console.error('Erro no logout:', error);
    }
    dispatch(eraseAll());
    sessionStorage.clear();
    localStorage.clear();
    if (savedTheme) localStorage.setItem(THEME_STORAGE_KEY, savedTheme);
    if (savedHint) localStorage.setItem(LIGHT_MODE_HINT_KEY, savedHint);
    navigate('/');
  };
  

  const handleShowResetPasswordModal = () => {
    setShowResetPassword(!showResetPassword);
  }

  return (
    <Container collapsed={collapsed}>
      <InnerContainer>
        <h1>Configurações</h1>

        <PreferenceSection>
          <PreferenceTitle>Notificações</PreferenceTitle>
          <ThemeDescription>
            Ative para receber e-mails automáticos do escaneamento FIRMS. Quando
            um risco é detectado, uma solicitação de alerta é criada e enviada
            ao seu e-mail para você criar o alerta correspondente.
          </ThemeDescription>
          <PreferenceToggleRow>
            <ToggleSwitch
              label="Receber e-mails automáticos FIRMS"
              checked={receiveFirmsEmails}
              onChange={handleFirmsToggle}
              disabled={loadingFirms || savingFirms}
            />
          </PreferenceToggleRow>
        </PreferenceSection>

        <ThemeSection $highlight={showThemeHint}>
          <PreferenceToggleRow $flush>
            <ToggleSwitch 
              label={
                <>
                  Light Mode
                  {showThemeHint ? <NewFeatureBadge>Novo</NewFeatureBadge> : null}
                </>
              }
              checked={!isDark}
              onChange={handleThemeToggle}
              disabled={isSavingTheme}
            />
          </PreferenceToggleRow>
          {showThemeHint ? (
            <ThemeTip role="status">
              <ThemeTipPointer aria-hidden>☝️</ThemeTipPointer>
              <span>
                <strong>Novidade:</strong> experimente o Light Mode — agora a
                plataforma também tem tema claro.
              </span>
              <ThemeTipDismiss type="button" onClick={dismissThemeHint}>
                Entendi
              </ThemeTipDismiss>
            </ThemeTip>
          ) : null}
        </ThemeSection>

        {currentUser?.user_type === "ADM" && (
          <NavBalloon
            type="button"
            onClick={() => navigate("/app_versions")}
          >
            Controle de Versão Guardian
          </NavBalloon>
        )}

        <ButtonContainer>
          <SettingsActionButton type="button" onClick={handleShowResetPasswordModal}>
            Alterar Senha
          </SettingsActionButton>
          <SettingsLogoutButton type="button" onClick={handleLogout}>
            Deslogar
          </SettingsLogoutButton>
        </ButtonContainer>

        {showResetPassword && <ResetPassword isOpen={showResetPassword} onClose={handleShowResetPasswordModal}/>}
      </InnerContainer>
    </Container>
  );
};

export default Settings;
